import { defineStore } from "pinia";
import { computed, ComputedRef, Ref, ref } from "vue";
import ApiService from "../composables/ApiService";
import { Price, Media, Product, ProductVariable, ProductVariation, ShippingTime } from "@daalder/types";
import { usePropertyHelper } from "../composables/PropertyHelper";
import collect, { Collection } from "collect.js";
import { useTranslation } from "../composables/Translation";
import { isArray } from "lodash-es";
import ProductJSONLD from "@daalder/types/dist/resources/ProductJSONLD";
import { usePriceHelper } from "../composables/PriceHelper";
import { useFormatsMoney } from "../composables/FormatsMoney";
import Cookies from "js-cookie";
import ShippingTimeService from "../composables/ShippingTimeService";

export const useProductDetailStore = defineStore("productDetail", () => {
    // Data
    const amountCounter: Ref<number> = ref(1);
    const productSku: Ref<string> = ref();
    const product: Ref<Product> = ref(null);
    const selectedProductVariables: Ref<{ [key: string]: any }> = ref({});
    const selectedWizardParts: Ref<
        {
            partId: number;
            name: string;
            amount: number;
            product: Product;
            alternativeId: number;
            sortOrder: number;
            price: Price;
        }[]
    > = ref([]);
    const sectionsIsExpanded: Ref<{ description: boolean; specstable: boolean }> = ref({
        description: false,
        specstable: false,
    });

    const initalIncludes: string[] = [
        "variations.values.product",
        "variations.values.product.stock",
        "variations.values.product.shipping_time",
        "variations.values.product.media.versions",
        "variations.values.variation_image",
        "properties.productattribute.productattributegroup",
        "brand.media.versions",
        "media.versions",
        "variations",
        "variables.options",
        "stock",
        "shipping_time",
        "group",
        "productattributeset.producttemplate",
        "sale_unit",
        "eligible_for_free_shipping",

        //@TODO: should be moved to a top level pinia store
        "has_exact_delivery_date",
    ];
    const backgroundIncludes: string[] = [
        "active_store_cross_sells.cross_sell_product.media.versions",
        "active_store_cross_sells.cross_sell_product.variations.values",
        "active_store_cross_sells.cross_sell_product.group",
        "active_store_cross_sells.cross_sell_product.properties",
        "active_store_combination_upsells.sub_parts.products.media.versions",
        "active_store_combination_upsells.sub_parts.products.shipping_time",
        "wizard",
        "categories",
        "active_store_compared",
        "wizard.subParts.alternatives.media.versions",
        "wizard.subParts.alternatives.product.media.versions",
        "wizard.subParts.alternatives.product.shipping_time",
    ];
    let productFetchHeaders: object = {};
    let apiService: ApiService = new ApiService();
    const shippingTimeService: ShippingTimeService = new ShippingTimeService();
    const productGallery: Ref<Media[]> = ref([]);

    // variable to store product mobile button props
    const stickyButton: Ref<{
        disabled?: boolean;
        url?: string;
        prevent?: boolean;
        fn?: () => void;
        text?: string;
        classes?: string;
        icon?: string;
        iconContent?: string;
        type?: string;
    }> = ref({});

    // array of booleans to list results of all elements intersection values tracked by observers
    const elementsObserveList: Ref<{ [x: string]: boolean }> = ref({});

    // loaders
    let productLoaders = {
        buySectionIsLoading: true,
        woodTableIsLoading: true,
        combiUpsellsIsLoading: true,
        alternativesIsLoading: true,
        crossSellsIsLoading: true,
        lastViewedIsLoading: true,
    };

    // Composables
    const propertyHelper = usePropertyHelper();
    const { __t } = useTranslation();
    const { getPrice, getLowestPriceAsObject, getLowestListPriceAsObject, getProductOptionsPrice, exclVat } =
        usePriceHelper();
    const { formatMoney } = useFormatsMoney();

    // Computed
    // TODO: This function can probably go, it was added for legacy support during the PDP unification
    const productHasCustomerPrice: ComputedRef<boolean> = computed(() => {
        return !!product.value?.prices?.find((price) => {
            // @ts-ignore
            return price.name === "customer_price";
        });
    });

    const shouldShowSpecificationsTable: ComputedRef<boolean> = computed(() => {
        return product.value?.properties?.length > 0 || !!product.value?.ean;
    });

    const lastViewedProductSkus: ComputedRef<string[]> = computed(() => {
        let lastViewedProducts = Cookies.get("daalder-last-viewed-products");
        return JSON.parse(lastViewedProducts ?? "[]") ?? [];
    });

    const shippingTime: ComputedRef<ShippingTime> = computed(() => {
        return shippingTimeService.latestShippingTime(allShippingTimes.value);
    });

    const allShippingTimes = computed(() => {
        const baseShippingTime: ShippingTime = product.value?.shipping_time;
        const partsShippingTimes = selectedWizardParts.value.flatMap((part) => part.product?.shipping_time || []);
        return [baseShippingTime, ...partsShippingTimes];
    });

    // TODO: We don't currently have a mapping for ProductProperties, but should add it at some point.
    // This computed property returns an object array per ProductAttributeGroup, with the group code and the properties
    // for that ProductAttributeGroup (with some exceptions).
    const specificationsTableProperties: ComputedRef<
        {
            name: string;
            code: string;
            properties: any[];
        }[]
    > = computed(() => {
        if (!product.value) {
            return [];
        }

        // Prepare the product id to always be shown on the specifications sheet
        let defaultProperties: { displayValue: string | number; attribute: any }[] = [
            {
                displayValue: product.value?.sku,
                attribute: {
                    name: __t("product.partials.specification.sku"),
                    productattributegroup: { code: "general" },
                },
            },
        ];

        // Prepare the product ean to always be shown on the specifications sheet (when available)
        if (product.value?.ean) {
            defaultProperties.push({
                displayValue: product.value.ean,
                attribute: {
                    name: __t("product.partials.specification.ean"),
                    productattributegroup: { code: "general" },
                },
            });
        }

        let nonDefaultProperties = collect(product.value?.properties ?? [])
            .map((property) => {
                // Remap properties under the 'specific' group to the 'general' group
                if (property?.attribute?.productattributegroup?.code == "specific") {
                    property.attribute.productattributegroup.code = "general";
                }

                // Fetch & set the 'display value' for the property.
                property.displayValue = getPropertyDisplayValue(property);

                return property;
            })
            .filter((property) => {
                let hasGroupCode = !!property?.attribute?.productattributegroup?.code;
                let shouldShowInSpecification = !!property.show_in_specifications;
                let hasDisplayValue = !!property.displayValue;

                return hasGroupCode && shouldShowInSpecification && hasDisplayValue;
            })
            .toArray();

        return collect(defaultProperties)
            .merge(nonDefaultProperties)
            .groupBy("attribute.productattributegroup.code")
            .map((properties, groupKey) => {
                // Get translation string for this group's name
                let name: string = __t(/* @trans-ignore */ `product.partials.specification.${groupKey}`);

                // If the translation string for this group's name has not been specified
                if (name == `product.partials.specification.${groupKey}`) {
                    // Fallback on the group's name from the API response
                    // @ts-ignore TODO: Remove this ts-ignore once we have proper typing support for ProductProperties
                    name = properties.first()?.attribute?.productattributegroup?.name;
                }

                return {
                    name: name,
                    code: groupKey,
                    properties: <any[]>properties,
                };
            })
            .toArray();
    });

    const minOrderAmount: ComputedRef<number> = computed(() => {
        return getLowestPriceAsObject(product.value?.prices)?.amount ?? 0;
    });

    const brandThumbnailSrc: ComputedRef<string> = computed(() => {
        if (!product.value?.brand?.media?.length) {
            return null;
        }

        if (product.value.brand.media[0]?.versions?.length > 1) {
            return product?.value.brand.media[0].versions[1].src;
        } else if (product.value.brand.media[0]?.versions?.length > 0) {
            return product?.value.brand.media[0].versions[0].src;
        }

        return product?.value.brand.media[0].src;
    });

    const productFiles: ComputedRef<Media[]> = computed(() => {
        return (product.value?.media ?? []).filter((media) => {
            return media.type.code === "file";
        });
    });

    const productMinimumQuantityPropertyValue: ComputedRef<number | null> = computed(() => {
        let minimalQuantity = propertyHelper.getProperty(product.value?.properties ?? [], "minimaleafname")?.value;

        return isNaN(minimalQuantity) ? null : parseFloat(minimalQuantity);
    });

    const productQuantityPerSquareMeter = computed(() => {
        let tilesQuantity = propertyHelper.getProperty(product.value?.properties ?? [], "aantalpm2")?.value;

        // get the number of tiles per square meter if the property exists, if not, check
        // if the product is sold per piece and calculate the number of tiles per square meter based on the property
        // minimum quantity to sell

        if (
            isNaN(tilesQuantity) &&
            propertyHelper.getProperty(product.value?.properties ?? [], "verkochtper")?.value_code === "pstuk"
        ) {
            tilesQuantity = Number((1 / productMinimumQuantityPropertyValue.value).toFixed(2));
        }

        return isNaN(tilesQuantity) ? null : parseFloat(tilesQuantity);
    });

    const sizeVariation: ComputedRef<ProductVariation | null> = computed(() => {
        return (
            product.value?.product_variations?.data?.find((variation) => {
                return variation.variation_type === "size";
            }) ?? null
        );
    });

    const productDetailPageTemplate: ComputedRef<"wood" | "stone" | "wizard" | "simple" | "deleted"> = computed(() => {
        if (!product.value) {
            return "simple";
        }

        // Get configured Product template
        let productTemplate: string =
            product.value.template ?? product.value.productattributeset?.product_template?.name ?? "simple";

        // Backwards compatibility, wood-new is deprecated, use wood.
        if (productTemplate == "wood-new") {
            productTemplate = "wood";
        }

        if (!!product.value.wizard && !!product.value.is_for_sale) {
            productTemplate = "wizard";
        }

        if (productTemplate == "Default") {
            productTemplate = "simple";
        }

        // Fallback to simple if no size variations are available
        if (productTemplate == "wood" && !sizeVariation.value) {
            productTemplate = "simple";
        }

        if (!!product.value.deleted_at || !product.value.is_for_sale) {
            if (productTemplate == "wood" && !!sizeVariation.value) {
                productTemplate = "wood";
            } else {
                productTemplate = "deleted";
            }
        }

        return <"wood" | "stone" | "wizard" | "simple" | "deleted">productTemplate;
    });

    // This computed property returns a boolean indicating whether the product's price exceeds the minimum offer price (product_min_offer_price)
    // and if any of the product's categories are included in the list of categories available for offers, as defined in the theme settings (product_category_available_for_offer).
    const isProductEligibleForQuoteRequest: ComputedRef<boolean> = computed(() => {
        const categories = product.value?.categories;

        if (!categories) {
            return false;
        }

        const categoryIds = categories.map((category) => category.id);

        const productCategoriesAvailableForOffer = window.product_category_available_for_offer;

        return productCategoriesAvailableForOffer.some((id: number) => categoryIds.includes(id));
    });

    const isProductEligibleToMakeAppointment: ComputedRef<boolean> = computed(() => {
        const categories = product.value?.categories;
        if (!categories) {
            return false;
        }
        const categoryIds = categories.map((category) => category.id);

        const productCategoriesAvailableToMakeAppointment = window.product_category_ids_available_for_make_appointment;

        return productCategoriesAvailableToMakeAppointment.some((id: number) => categoryIds.includes(id));
    });

    const canProductSkipConfirmationModal: ComputedRef<boolean> = computed(() => {
        const categories = product.value?.categories;

        if (!categories) {
            return false;
        }

        const categoryIds = categories.map((category) => category.id);

        return window.product_category_to_skip_add_to_basket_modal?.some((id: number) => categoryIds.includes(id));
    });

    const formattedPriceWithoutDiscount: ComputedRef<string | null> = computed(() => {
        let listPrice = getLowestListPriceAsObject(product.value?.prices, amountCounter.value);

        let optionsPrice = getProductOptionsPrice(product.value?.variables ?? [], selectedProductVariables.value);

        if (listPrice) {
            listPrice = exclVat() ? listPrice.list_price_excluding_vat : listPrice.list_price;

            return formatMoney(
                parseFloat(listPrice.toString()) + parseFloat(optionsPrice.toString()),
                listPrice.currency,
                true,
                false,
            );
        }

        let originalPrice = getLowestPriceAsObject(product.value?.prices, amountCounter.value);

        if (originalPrice) {
            originalPrice = exclVat() ? originalPrice.original_price_excluding_vat : originalPrice.original_price;

            if (originalPrice && originalPrice != 0) {
                return formatMoney(
                    parseFloat(originalPrice.toString()) + parseFloat(optionsPrice.toString()),
                    originalPrice.currency,
                    true,
                    false,
                );
            }
        }

        return null;
    });

    const discountAmount: ComputedRef<string> = computed(() => {
        if (product.value?.prices?.[0].price_type.type === "default") {
            if (product.value?.prices[0].discount_type === "percentage") {
                return product.value?.prices[0].discount_amount + "%";
            }

            if (product.value?.prices[0].discount_type === "amount") {
                return formatMoney(product.value.prices[0].discount_amount);
            }
        }

        return null;
    });

    const disabledBuyButton: ComputedRef<boolean> = computed(() => {
        // If the amount is too small
        if (amountCounter.value < minOrderAmount.value) {
            return true;
        }

        // If the product is not procured_on_demand and the chosen amount is greater than the available stock.
        return !product.value?.stock?.is_procured_on_demand && amountCounter.value > product.value?.stock?.in_stock;
    });

    // when intersection is passed adjust the observerList
    const checkIfElementsAreInView = (entries: IntersectionObserverEntry[]) => {
        entries.forEach((entry: IntersectionObserverEntry) => {
            elementsObserveList.value[entry.target.id] = entry.isIntersecting;
        });
    };

    // creates observers
    const elementsObserver: IntersectionObserver = new IntersectionObserver(checkIfElementsAreInView, {
        rootMargin: "-60px 0px -58px 0px",
    });

    // Methods
    function getJSONLD(): ProductJSONLD {
        let productJSONLD: string = document.querySelector(
            'script[type="application/ld+json"].product-jsonld',
        )?.innerHTML;

        return JSON.parse(productJSONLD ?? "{}");
    }

    async function init(additionalHeaders: object): Promise<Product> | null {
        // TODO: support 'no price rounding' on wood template. We'd already need the product template, at this point.
        // if(productDetailPageTemplate.value === 'wood') {
        // await productDetailStore.init({ "X-Daalder-Price-Rounding": 0 });
        // }

        productFetchHeaders = Object.assign(productFetchHeaders, additionalHeaders);

        let productJSONLD = getJSONLD();

        if (productJSONLD?.sku) {
            productSku.value = productJSONLD?.sku;

            return fetchProduct();
        }

        // If there is no Product JSON+LD, this must not be a product detail page.
        return null;
    }

    async function fetchProduct(altMode: boolean = false): Promise<Product> {
        let url = altMode
            ? `/v2/products?filter[sku]=${productSku.value}&includes=${initalIncludes.join(",")}&with_trashed=true`
            : `/v2/products/sku/${productSku.value}?includes=${initalIncludes.join(",")}`;

        return apiService
            .get(url, productFetchHeaders)
            .then((response) => {
                product.value = altMode ? response?.data?.data?.products?.[0] ?? null : response.data ?? null;

                if (!product.value.is_for_sale) {
                    amountCounter.value = 0;
                }

                initVariables();

                // This will run in the background (async)
                fetchProductWithAdditionalIncludes();

                return product.value;
            })
            .catch((error) => {
                if (error.response.status === 404) {
                    return fetchProduct(true);
                } else {
                    throw error;
                }
            });
    }

    // This method fetches additional data for the product in the background, to speed up the time-to-interactive for
    // PDPs. The additional data is merged into the product member variable.
    async function fetchProductWithAdditionalIncludes(altMode: boolean = false): Promise<Product> {
        let url = altMode
            ? `/v2/products?filter[sku]=${productSku.value}&includes=${backgroundIncludes.join(",")}&with_trashed=true`
            : `/v2/products/sku/${productSku.value}?includes=${backgroundIncludes.join(",")}`;

        return apiService
            .get(url, productFetchHeaders)
            .then((response) => {
                let additionalProductData: Product = altMode
                    ? response?.data?.data?.products?.[0] ?? null
                    : response.data ?? null;

                product.value = { ...product.value, ...additionalProductData };

                return product.value;
            })
            .catch((error) => {
                if (error.response.status === 404) {
                    return fetchProductWithAdditionalIncludes(true);
                } else {
                    throw error;
                }
            });
    }

    function initVariables() {
        // This (apparently) prevents cached options from appearing on the page as 'selected' (ported from
        // wordpress-unified).
        product.value.variables?.forEach((variable: ProductVariable) => {
            if (variable.type === "dropdown") {
                if (variable.required && variable.options[0]) {
                    selectedProductVariables.value[variable.id] = variable.options[0].id;
                } else {
                    selectedProductVariables.value[variable.id] = "";
                }
            } else {
                if (variable.required && variable.options[0]) {
                    selectedProductVariables.value[variable.id] = [variable.options[0].id];
                } else {
                    selectedProductVariables.value[variable.id] = [];
                }
            }
        });
    }

    // TODO: This is a remnant from the PDP unification. This logic used to exist in PHP, and was ported with some
    // TODO: optimalizations. It should probably be removed in favor of a more Vue-way approach.
    function getPropertyDisplayValue(property: any): string | null {
        // Prepare some checks to see if the property can have a valid 'display value'
        let propertyHasInvalidArrayValue = isArray(property.value) && property.value?.[0] == "";
        let propertyHasNoSelectValue = property.attribute.inputtype == "select" && !property.value;
        let propertyIsProsCons = property.attribute.code === "pros" || property.attribute.code === "cons";

        let displayValue = null;

        // If the property can have a valid 'display value'
        if (!propertyHasInvalidArrayValue && !propertyHasNoSelectValue && !propertyIsProsCons) {
            if (property.attribute.inputtype == "boolean") {
                // Parse boolean value to translatable string
                displayValue =
                    property.value == 1 ? "\u2713 " + __t("properties.yes") : "\u2717 " + __t("properties.no");
            } else if (isArray(property.value) && !!property.value) {
                // Implode (multi-)select value
                displayValue = property.value.join(", ");
            } else if (property.value) {
                // Fallback to single string/number value
                displayValue = property.value;
            }

            // If the property has an unit, add it to the 'display value' string
            if (displayValue && property.attribute.unit) {
                displayValue = displayValue + " " + property.attribute.unit;
            }
        }

        // Return 'display value'
        return displayValue;
    }

    function isMinimumQuotePriceReached(amount: number = amountCounter.value): boolean {
        if (isProductEligibleForQuoteRequest.value) {
            const price = getPrice(product.value.prices, amount);
            const minOfferPrice = window.product_min_offer_price as number;

            return price * amount > minOfferPrice;
        }

        return false;
    }

    function foldOpenSection(section: "description" | "specstable") {
        sectionsIsExpanded.value[section] = true;
    }

    function scrollToSection(section: "description" | "specstable" | "downloads" | "woodtable") {
        if (section === "description" || section === "specstable") {
            foldOpenSection(section);
        }

        let sectionEl: HTMLElement = document.getElementById(`pdp-${section}-wrapper`);

        if (sectionEl) {
            let sectionY: number = sectionEl.getBoundingClientRect().y;
            let headerHeight: number = document.querySelector("header").clientHeight;
            window.scrollTo(0, sectionY + window.scrollY - headerHeight - 20);
        }
    }

    function findVariationProductById(id: number): Product | null {
        return <Product | null>(product.value?.product_variations?.products?.find((product) => {
            return product.id === id;
        }) ?? null);
    }

    // method to add observer to element passed
    function addToElementObserver(target: HTMLElement) {
        elementsObserveList.value[target.id] = false;
        elementsObserver.observe(target);
    }

    return {
        product,
        selectedProductVariables,
        selectedWizardParts,
        shippingTime,
        allShippingTimes,
        sizeVariation,
        productDetailPageTemplate,
        productHasCustomerPrice,
        productMinimumQuantityPropertyValue,
        productQuantityPerSquareMeter,
        productFiles,
        sectionsIsExpanded,
        isProductEligibleForQuoteRequest,
        isProductEligibleToMakeAppointment,
        canProductSkipConfirmationModal,
        isMinimumQuotePriceReached,
        formattedPriceWithoutDiscount,
        discountAmount,
        minOrderAmount,
        disabledBuyButton,
        brandThumbnailSrc,
        shouldShowSpecificationsTable,
        lastViewedProductSkus,
        specificationsTableProperties,
        init,
        amountCounter,
        getJSONLD,
        foldOpenSection,
        scrollToSection,
        findVariationProductById,
        addToElementObserver,
        elementsObserveList,
        stickyButton,
        productLoaders,
        productGallery,
    };
});
